// pages/ContractsOnboardingPage/hooks/useContractReviewState.ts
import { useState, useCallback } from 'react';
import { getReviewState } from '../../../core-utils/axios/compliance-assurance/contracts';
import {
  ReviewBuckets,
  ReviewCounts,
  ReviewStateValue,
} from '../../types/contracts.types';

const EMPTY_COUNTS: ReviewCounts = {
  processing: 0,
  ready_to_review: 0,
  failed: 0,
  reviewed: 0,
  total: 0,
};
const EMPTY_BUCKETS: ReviewBuckets = {
  processing: [],
  readyToReview: [],
  failed: [],
  reviewed: [],
};

export const useContractReviewState = ({ egNid }: { egNid: string }) => {
  const [state, setState] = useState<ReviewStateValue>('processing');
  const [counts, setCounts] = useState<ReviewCounts>(EMPTY_COUNTS);
  const [documents, setDocuments] = useState<ReviewBuckets>(EMPTY_BUCKETS);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReviewState = useCallback(async (): Promise<ReviewStateValue | null> => {
    if (!egNid) {
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getReviewState(egNid);
      setState(data?.state ?? 'processing');
      setCounts(data?.counts ?? EMPTY_COUNTS);
      setDocuments(data?.documents ?? EMPTY_BUCKETS);
      return data?.state ?? null;
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err?.response?.data?.error || 'Failed to fetch status');
      return null;
    } finally {
      setLoading(false);
    }
  }, [egNid]);

  // No internal useEffect — the page triggers the single fetch and decides
  // the stage. Exposed as refresh() for both the mount call and the button.
  return {
    state,
    counts,
    documents,
    loading,
    error,
    refresh: fetchReviewState,
  };
};
